import React, { useEffect, useState } from "react"
import { Stage } from "../../../../types/brandDatas"
import { defaultStages } from "../../../../static/Brand"

interface StagesModalProps {
  isOpen: boolean
  onClose: () => void
  brandId: string
  stages: Stage[]
  onStageChange: (brandId: string, stages: Stage[]) => void
}

const StagesModal: React.FC<StagesModalProps> = ({
  isOpen,
  onClose,
  brandId,
  stages,
  onStageChange,
}) => {
  const [selectedStage, setSelectedStage] = useState("")

  useEffect(() => {
    if (stages && stages.length > 0) {
      setSelectedStage(stages[0].stage)
    } else {
      setSelectedStage("")
    }
  }, [stages, isOpen])

  const handleSaveStage = () => {
    if (selectedStage) {
      const newStages = [{ stage: selectedStage, stage_time: new Date() }, ...(stages || [])]
      onStageChange(brandId, newStages)
      onClose()
      setSelectedStage("")
    }
  }

  const getStageDisplayName = (stageKey: string) => {
    const stage = defaultStages.find((s) => s.key === stageKey)
    return stage ? stage.display : stageKey
  }

  if (!isOpen) {
    return null
  }

  return (
    <div className="fixed right-0 top-0 h-full z-50">
      <div className="bg-white h-full w-96 shadow-lg flex flex-col">
        {/* Header */}
        <div className="bg-gradient-to-r from-blue-600 to-purple-600 text-white p-4">
          <div className="flex justify-between items-start">
            <div>
              <h2 className="text-xl font-semibold">Marka Aşaması</h2>
              <p className="text-sm opacity-90 mt-1">Marka ID: {brandId}</p>
            </div>
            <button onClick={onClose} className="text-white hover:text-gray-200">
              ×
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 p-4 overflow-y-auto">
          {/* Current Stage Display */}
          {stages && stages.length > 0 && (
            <div className="mb-6">
              <h3 className="text-sm font-medium text-gray-700 mb-2">Mevcut Aşama</h3>
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                <p className="text-blue-800 font-semibold text-sm">
                  {getStageDisplayName(stages[0].stage)}
                </p>
                <p className="text-blue-600 text-xs mt-1">
                  {new Date(stages[0].stage_time).toLocaleString("tr-TR")}
                </p>
              </div>
            </div>
          )}

          {/* Stage History */}
          {stages && stages.length > 1 && (
            <div className="mb-6">
              <h3 className="text-sm font-medium text-gray-700 mb-2">Aşama Geçmişi</h3>
              <div className="space-y-2 max-h-32 overflow-y-auto">
                {stages.slice(1).map((stage, index) => (
                  <div key={index} className="bg-gray-50 border border-gray-200 rounded-lg p-2">
                    <p className="text-gray-800 font-medium text-sm">
                      {getStageDisplayName(stage.stage)}
                    </p>
                    <p className="text-gray-600 text-xs">
                      {new Date(stage.stage_time).toLocaleString("tr-TR")}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Stage Selection */}
          <div>
            <h3 className="text-sm font-medium text-gray-700 mb-2">
              {stages && stages.length > 0 ? "Aşamayı Değiştir" : "Aşama Seç"}
            </h3>
            <select
              value={selectedStage}
              onChange={(e) => setSelectedStage(e.target.value)}
              className="w-full p-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">Aşama seçin</option>
              {defaultStages.map((stage) => (
                <option key={stage.key} value={stage.key}>
                  {stage.display}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Footer */}
        <div className="border-t p-4 bg-gray-50">
          <div className="flex justify-between items-center">
            <div className="text-sm text-gray-600">
              {stages ? `${stages.length} aşama geçmişi` : "Aşama geçmişi yok"}
            </div>
            <div className="flex gap-2">
              <button
                onClick={onClose}
                className="px-4 py-2 text-gray-600 border border-gray-300 rounded-md hover:bg-gray-50"
              >
                İptal
              </button>
              <button
                onClick={handleSaveStage}
                disabled={!selectedStage}
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
              >
                Kaydet
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default StagesModal
